import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { aiConversationsApi } from '@/api';
import { queryKeys } from '@/api/query-keys';
import type {
  AssistantConversationDetail,
  AssistantConversationSummary,
  AssistantMessageRating,
} from '@/api/ai/ai.types';

/** Lists the caller's own conversations, newest activity first. */
export const useAiConversations = (
  options?: Partial<
    UseQueryOptions<AssistantConversationSummary[], AxiosError>
  >,
) =>
  useQuery<AssistantConversationSummary[], AxiosError>({
    queryKey: queryKeys.aiConversations.all,
    queryFn: () => aiConversationsApi.list().then((r) => r.data),
    ...options,
  });

/** Loads one conversation with its full message history. */
export const useAiConversation = (
  id: string | null,
  options?: Partial<UseQueryOptions<AssistantConversationDetail, AxiosError>>,
) =>
  useQuery<AssistantConversationDetail, AxiosError>({
    queryKey: id ? queryKeys.aiConversations.detail(id) : ['aiConversations', 'null'],
    queryFn: () => aiConversationsApi.get(id as string).then((r) => r.data),
    enabled: !!id,
    ...options,
  });

export const useRenameAiConversation = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    { id: string; title: string | null }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { id: string; title: string | null }>({
    mutationFn: async ({ id, title }) => {
      await aiConversationsApi.rename(id, title);
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.aiConversations.all,
      });
      const [, vars] = args;
      queryClient.invalidateQueries({
        queryKey: queryKeys.aiConversations.detail(vars.id),
      });
      options?.onSuccess?.(...args);
    },
  });
};

export const useDeleteAiConversation = (
  options?: UseMutationOptions<void, AxiosError, { id: string }>,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, { id: string }>({
    mutationFn: async ({ id }) => {
      await aiConversationsApi.delete(id);
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.aiConversations.all,
      });
      const [, vars] = args;
      queryClient.removeQueries({
        queryKey: queryKeys.aiConversations.detail(vars.id),
      });
      options?.onSuccess?.(...args);
    },
  });
};

/**
 * Sets or clears (`rating: null`) the thumbs-up / thumbs-down on one
 * assistant message. The mutation invalidates the parent conversation so any
 * loaded panel re-reads the persisted rating, but the rating buttons are
 * also optimistically toggled by the panel's local state.
 */
export const useRateAiMessage = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    { messageId: string; rating: AssistantMessageRating; conversationId?: string }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    AxiosError,
    { messageId: string; rating: AssistantMessageRating; conversationId?: string }
  >({
    mutationFn: async ({ messageId, rating }) => {
      await aiConversationsApi.rate(messageId, rating);
    },
    ...options,
    onSuccess: (...args) => {
      const [, vars] = args;
      if (vars.conversationId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.aiConversations.detail(vars.conversationId),
        });
      }
      options?.onSuccess?.(...args);
    },
  });
};
